import { useState, useEffect } from "react";
import { AxiosError } from "axios";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { getGlobalSettings, updateGlobalSettings } from "@/zapi-admin/shared/services/globalSettings";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { MODEL_OPTIONS } from "@/zapi-admin/shared/constants/models";

const PERFORMANCE_FIELDS = [
  { key: "maxConcurrentAiCallsGpt", label: "Max concurrent AI calls — GPT" },
  { key: "maxConcurrentAiCallsClaude", label: "Max concurrent AI calls — Claude" },
  { key: "maxConcurrentAiCallsGemini", label: "Max concurrent AI calls — Gemini" },
  { key: "maxConcurrentAiCallsOther", label: "Max concurrent AI calls — Other" },
  { key: "maxConcurrentDbCalls", label: "Max concurrent DB calls" }
] as const;

type TPerformanceField = (typeof PERFORMANCE_FIELDS)[number]["key"];
type TPerformanceValues = Record<TPerformanceField, string>;

export function GlobalSettingsPage() {
  const [defaultOpenAiModel, setDefaultOpenAiModel] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingPerformance, setIsEditingPerformance] = useState(false);
  const [performanceValues, setPerformanceValues] = useState<TPerformanceValues>({
    maxConcurrentAiCallsGpt: "",
    maxConcurrentAiCallsClaude: "",
    maxConcurrentAiCallsGemini: "",
    maxConcurrentAiCallsOther: "",
    maxConcurrentDbCalls: ""
  });

  const {
    data: globalSettings,
    isLoading,
    refetch
  } = useQuery({
    queryKey: ["globalSettings"],
    queryFn: getGlobalSettings
  });

  useEffect(() => {
    if (globalSettings) {
      setDefaultOpenAiModel(globalSettings.defaultOpenAiModel);
    }
  }, [globalSettings]);

  const syncPerformanceValuesFromSettings = () => {
    if (!globalSettings) return;
    setPerformanceValues({
      maxConcurrentAiCallsGpt: String(globalSettings.maxConcurrentAiCallsGpt),
      maxConcurrentAiCallsClaude: String(globalSettings.maxConcurrentAiCallsClaude),
      maxConcurrentAiCallsGemini: String(globalSettings.maxConcurrentAiCallsGemini),
      maxConcurrentAiCallsOther: String(globalSettings.maxConcurrentAiCallsOther),
      maxConcurrentDbCalls: String(globalSettings.maxConcurrentDbCalls)
    });
  };

  useEffect(() => {
    syncPerformanceValuesFromSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalSettings]);

  const performanceMutation = useMutation({
    mutationFn: updateGlobalSettings,
    onSuccess: () => {
      toast.success("Performance limits updated successfully");
      setIsEditingPerformance(false);
      refetch();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      const errorMessage = error.response?.data?.message || "Failed to update performance limits";
      toast.error(errorMessage);
      syncPerformanceValuesFromSettings();
    }
  });

  const handleSavePerformance = () => {
    const parsed: Partial<Record<TPerformanceField, number>> = {};
    for (const field of PERFORMANCE_FIELDS) {
      const value = Number(performanceValues[field.key]);
      if (!Number.isInteger(value) || value < 1 || value > 500) {
        toast.error(`${field.label} must be an integer between 1 and 500.`);
        return;
      }
      parsed[field.key] = value;
    }
    performanceMutation.mutate(parsed);
  };

  const handleCancelPerformance = () => {
    syncPerformanceValuesFromSettings();
    setIsEditingPerformance(false);
  };

  const mutation = useMutation({
    mutationFn: updateGlobalSettings,
    onSuccess: () => {
      toast.success("Global settings updated successfully");
      setIsEditing(false);
      refetch();
    },
    onError: (error: AxiosError<{ message: string }>) => {
      const errorMessage = error.response?.data?.message || "Failed to update global settings";
      toast.error(errorMessage);
      if (globalSettings) {
        setDefaultOpenAiModel(globalSettings.defaultOpenAiModel);
      }
    }
  });

  const handleSave = () => {
    const modelToSave = defaultOpenAiModel.trim();
    if (modelToSave) {
      // Validate that the selected model is in our allowed list
      const isValidModel = MODEL_OPTIONS.some((option) => option.value === modelToSave);
      if (!isValidModel) {
        toast.error("Invalid model selected. Please choose a valid AI model from the dropdown.");
        return;
      }
      mutation.mutate({ defaultOpenAiModel: modelToSave });
    } else {
      toast.error("Please select a model before saving.");
    }
  };

  const handleCancel = () => {
    if (globalSettings) {
      setDefaultOpenAiModel(globalSettings.defaultOpenAiModel);
    }
    setIsEditing(false);
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <FigmaTypography component="h1" variant="heading-1" className="mb-8">Global Settings</FigmaTypography>
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  return (
    <div className="container mx-auto py-8 px-4" data-testid="global-settings-page">
      <div className="max-w-2xl mx-auto">
        <FigmaTypography component="h1" variant="heading-1" className="mb-8" data-testid="global-settings-heading">Global Settings</FigmaTypography>

        <Card style={{ padding: "16px" }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">Default AI Model</CardTitle>
            <CardDescription>
              Configure the default AI model to be used for AI operations across the system. Only valid models from the
              supported list are allowed.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {!isEditing ? (
              <div className="space-y-4">
                <div className="min-h-[40px] flex items-center">
                  <div className="flex items-center gap-3">
                    <code className="text-[14px] bg-gray-100 px-3 py-2 rounded border">
                      {globalSettings?.defaultOpenAiModel || "Loading..."}
                    </code>
                  </div>
                </div>

                <div className="flex gap-2">
                  <AppButton appearance="outlined" onClick={() => setIsEditing(true)} data-testid="global-settings-edit-button">
                    Edit
                  </AppButton>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label htmlFor="openai-model" className="block text-[14px] font-medium text-gray-700 mb-2">
                    AI Model
                  </label>
                  <Select
                    value={defaultOpenAiModel}
                    onValueChange={setDefaultOpenAiModel}
                    disabled={mutation.isPending}
                  >
                    <SelectTrigger className="w-full" data-testid="global-settings-model-select">
                      <SelectValue placeholder="Select an AI model..." />
                    </SelectTrigger>
                    <SelectContent>
                      {MODEL_OPTIONS.map((model) => (
                        <SelectItem key={model.value} value={model.value}>
                          {model.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex gap-2">
                  <AppButton appearance="outlined" onClick={handleSave} disabled={mutation.isPending} data-testid="global-settings-save-button">
                    {mutation.isPending ? "Saving..." : "Save"}
                  </AppButton>
                  <AppButton appearance="outlined" onClick={handleCancel} disabled={mutation.isPending} data-testid="global-settings-cancel-button">
                    Cancel
                  </AppButton>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card style={{ padding: "16px", marginTop: "24px" }}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">Performance Limits</CardTitle>
            <CardDescription>
              Concurrency caps applied at runtime across all backend instances. AI call limits are per provider. Changes
              take effect immediately without a redeploy.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {!isEditingPerformance ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  {PERFORMANCE_FIELDS.map((field) => (
                    <div key={field.key} className="flex items-center justify-between gap-3">
                      <span className="text-[14px] text-gray-700">{field.label}</span>
                      <code className="text-[14px] bg-gray-100 px-3 py-1 rounded border">
                        {globalSettings ? globalSettings[field.key] : "—"}
                      </code>
                    </div>
                  ))}
                </div>

                <div className="flex gap-2">
                  <AppButton
                    appearance="outlined"
                    onClick={() => setIsEditingPerformance(true)}
                    data-testid="performance-limits-edit-button"
                  >
                    Edit
                  </AppButton>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {PERFORMANCE_FIELDS.map((field) => (
                  <div key={field.key}>
                    <label htmlFor={field.key} className="block text-[14px] font-medium text-gray-700 mb-2">
                      {field.label}
                    </label>
                    <Input
                      id={field.key}
                      type="number"
                      min={1}
                      max={500}
                      value={performanceValues[field.key]}
                      disabled={performanceMutation.isPending}
                      onChange={(e) =>
                        setPerformanceValues((prev) => ({ ...prev, [field.key]: e.target.value }))
                      }
                    />
                  </div>
                ))}

                <div className="flex gap-2">
                  <AppButton
                    appearance="outlined"
                    onClick={handleSavePerformance}
                    disabled={performanceMutation.isPending}
                    data-testid="performance-limits-save-button"
                  >
                    {performanceMutation.isPending ? "Saving..." : "Save"}
                  </AppButton>
                  <AppButton
                    appearance="outlined"
                    onClick={handleCancelPerformance}
                    disabled={performanceMutation.isPending}
                    data-testid="performance-limits-cancel-button"
                  >
                    Cancel
                  </AppButton>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
